import React, { useState } from 'react'
import backgroundImage from '@assets/image.png'
import { cmsService } from '@services/api'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Card,
  CardBody,
  CardHeader,
  Center,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider,
  Badge,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import { 
  Trash2, 
  Shield, 
  Clock, 
  CheckCircle,
  Mail,
  Phone,
  Lock
} from 'lucide-react'

export default function DeleteAccountPage() {
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    reason: '',
    confirmDeletion: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const toast = useToast()

  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await cmsService.requestAccountDeletion({
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        reason: formData.reason || undefined
      })
      
      if (response.success) {
        setIsSubmitted(true)
        toast({
          title: 'Account Deletion Request Submitted',
          description: response.message || 'Your account will be deleted within 7 days. You will receive a confirmation email.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || error?.message || 'Failed to submit deletion request. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (isSubmitted) {
    return (
      <Box
        minH="100vh"
        backgroundImage={`url(${backgroundImage})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundAttachment="fixed"
        py={8}
        position="relative"
      >
        {/* Overlay for better readability */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.4)"
          zIndex={1}
        />
        <Center position="relative" zIndex={2}>
          <Card maxW="600px" w="full" bg={cardBg} shadow="lg">
            <CardBody p={8}>
              <VStack spacing={6} textAlign="center">
                <Box p={4} bg="green.100" borderRadius="full">
                  <CheckCircle size={48} color="var(--chakra-colors-green-500)" />
                </Box>
                
                <VStack spacing={4}>
                  <Heading size="lg" color="green.600">
                    Account Deletion Request Submitted
                  </Heading>
                  
                  <Text color="gray.600" fontSize="lg">
                    Your account deletion request has been successfully submitted.
                  </Text>
                  
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>7-Day Grace Period</AlertTitle>
                      <AlertDescription>
                        Your account will be permanently deleted within 7 days. You can cancel this request by logging into your account during this period.
                      </AlertDescription>
                    </Box>
                  </Alert>
                  
                  <VStack spacing={3} align="start" w="full">
                    <HStack spacing={3}>
                      <Mail size={20} color="var(--chakra-colors-blue-500)" />
                      <Text>Confirmation email sent to {formData.email}</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Clock size={20} color="var(--chakra-colors-orange-500)" />
                      <Text>Deletion scheduled for 7 days from now</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Shield size={20} color="var(--chakra-colors-green-500)" />
                      <Text>All your data will be permanently removed</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </Center>
      </Box>
    )
  }

  return (
    <Box
      minH="100vh"
      backgroundImage={`url(${backgroundImage})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundAttachment="fixed"
      py={8}
      position="relative"
    >
      {/* Overlay for better readability */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.4)"
        zIndex={1}
      />
      <Center position="relative" zIndex={2}>
        <Card maxW="600px" w="full" bg={cardBg} shadow="lg">
          <CardHeader textAlign="center" pb={4}>
            <VStack spacing={4}>
              <Box p={4} bg="red.100" borderRadius="full">
                <Trash2 size={48} color="var(--chakra-colors-red-500)" />
              </Box>
              
              <VStack spacing={2}>
                <Heading size="xl" color="gray.900">
                  Delete Your Account
                </Heading>
                <Text color="gray.600" fontSize="lg">
                  Request permanent deletion of your Daladala Smart account
                </Text>
              </VStack>
            </VStack>
          </CardHeader>

          <CardBody pt={0}>
            <VStack spacing={6} align="stretch">
              {/* Warning Alert */}
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>This action cannot be undone</AlertTitle>
                  <AlertDescription>
                    Once your account is deleted, all your data including bookings, payment history, and preferences will be permanently removed.
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Process Information */}
              <Card bg="blue.50" border="1px solid" borderColor="blue.200">
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack spacing={3}>
                      <Clock size={20} color="var(--chakra-colors-blue-600)" />
                      <Text fontWeight="semibold" color="blue.800">7-Day Grace Period</Text>
                    </HStack>
                    <Text color="blue.700" fontSize="sm">
                      Your account will be scheduled for deletion within 7 days. During this period, you can log in and cancel the deletion request if you change your mind.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Deletion Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={5} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Mail size={16} />
                        <Text>Email Address</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your registered email address"
                      size="lg"
                      borderRadius="xl"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Phone size={16} />
                        <Text>Phone Number</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Enter your registered phone number"
                      size="lg"
                      borderRadius="xl"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>
                      <HStack spacing={2}>
                        <Lock size={16} />
                        <Text>Password</Text>
                      </HStack>
                    </FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter your account password"
                      size="lg"
                      borderRadius="xl"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Reason for Deletion (Optional)</FormLabel>
                    <Textarea
                      value={formData.reason}
                      onChange={(e) => handleInputChange('reason', e.target.value)}
                      placeholder="Help us improve by telling us why you're leaving..."
                      size="lg"
                      borderRadius="xl"
                      rows={4}
                    />
                  </FormControl>

                  <Divider />

                  {/* Confirmation Checkbox */}
                  <Card bg="red.50" border="1px solid" borderColor="red.200">
                    <CardBody>
                      <HStack spacing={3} align="start">
                        <input
                          type="checkbox"
                          checked={formData.confirmDeletion}
                          onChange={(e) => handleInputChange('confirmDeletion', e.target.checked)}
                          style={{ marginTop: '4px' }}
                        />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold" color="red.800">
                            I understand the consequences
                          </Text>
                          <Text fontSize="sm" color="red.700">
                            I confirm that I want to permanently delete my account and all associated data. 
                            This action cannot be undone after the 7-day grace period.
                          </Text>
                        </VStack>
                      </HStack>
                    </CardBody>
                  </Card>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    colorScheme="red"
                    size="lg"
                    borderRadius="xl"
                    isLoading={isLoading}
                    loadingText="Submitting Request..."
                    isDisabled={!formData.email || !formData.phoneNumber || !formData.password || !formData.confirmDeletion}
                    leftIcon={<Trash2 size={20} />}
                  >
                    Request Account Deletion
                  </Button>
                </VStack>
              </form>

              {/* Additional Information */}
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack spacing={3} align="start">
                    <Text fontWeight="semibold" color="gray.800">What happens next?</Text>
                    <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
                      <Text>• You'll receive a confirmation email within 24 hours</Text>
                      <Text>• Your account will be deactivated immediately</Text>
                      <Text>• All data will be permanently deleted after 7 days</Text>
                      <Text>• You can cancel the deletion by logging in during the grace period</Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </CardBody>
        </Card>
      </Center>
    </Box>
  )
}
